import React from 'react';
import { Route, Redirect, Switch } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { bindActionCreators, compose } from 'redux';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import browserHistory from 'components/utility/history';
import { APIsRoute } from './apidocs';
import { ConfigurationRoute } from './configuration';
import { CredentialsRoute } from './credentials';
import { ComponentsRoute } from './components';
import { FlowsRoute } from './flows';
import { ModelsRoute } from './models';
import summary, { SummaryRoute } from './summary';
import navigation from './navigation';
import { UpdatesRoute } from './updates';
import system from './system';

class ProjectRoute extends React.Component {
	constructor(props) {
		super(props);
		IntlComponent.init(this);
		// Publish the location to the store
		browserHistory.listen(props.setLocation);

		// generic error strings
		this.intl.formatMsg('Not found', 'error.title.notfound.404');
		this.intl.formatMsg('Unable to complete requested action', 'error.summary.notfound');
		this.intl.formatMsg('Unable to complete requested action', 'error.summary.generic');
		this.intl.formatMsg('Cannot find resource', 'error.title.notfound.generic');
		this.intl.formatMsg('Cannot find resource', 'error.detail.notfound.404');
		this.intl.formatMsg('Cannot find resource', 'error.detail.notfound.generic');
		this.intl.formatMsg('Cannot find resource', 'error.detail.404');
		this.intl.formatMsg('Unknown error', 'error.detail.generic');

		// SAVE_CONFIG error strings
		this.intl.formatMsg('Unable to save configuration changes to the API Builder server.', 'error.summary.SAVE_CONFIG');

		// FETCH_CONFIG error strings
		this.intl.formatMsg('Error loading config', 'error.title.FETCH_CONFIG.404');
		this.intl.formatMsg('Unable to retrieve server configuration.', 'error.summary.FETCH_CONFIG');
		this.intl.formatMsg('The configuration file may no longer exist', 'error.detail.FETCH_CONFIG.404');
		this.intl.formatMsg('Unknown error', 'error.detail.FETCH_CONFIG.generic');

		// LIST_CONFIGS error strings
		this.intl.formatMsg('Error loading configurations', 'error.title.LIST_CONFIGS.404');
		this.intl.formatMsg('Unable to complete requested action', 'error.summary.LIST_CONFIGS');
		this.intl.formatMsg('Unable to retrieve server configuration', 'error.detail.LIST_CONFIGS.404');
		this.intl.formatMsg('Unknown error', 'error.detail.LIST_CONFIGS.generic');

		// FETCH_GROUP_SUMMARIES error strings
		this.intl.formatMsg('Error loading API group summaries', 'error.title.FETCH_GROUP_SUMMARIES.404');
		this.intl.formatMsg('Unable to complete requested action', 'error.summary.FETCH_GROUP_SUMMARIES');
		this.intl.formatMsg('The API group summaries failed to load', 'error.detail.FETCH_GROUP_SUMMARIES.404');
		this.intl.formatMsg('Unknown error', 'error.detail.FETCH_GROUP_SUMMARIES.generic');

		// FETCH_CONNECTORS error strings
		this.intl.formatMsg('Error loading connectors', 'error.title.FETCH_CONNECTORS.404');
		this.intl.formatMsg('Unable to complete requested action', 'error.summary.FETCH_CONNECTORS');
		this.intl.formatMsg('The connectors may no longer exist', 'error.detail.FETCH_CONNECTORS.404');
		this.intl.formatMsg('Unknown error', 'error.detail.FETCH_CONNECTORS.generic');

		// FETCH_COMPONENTS
		// TODO(MP): After we rename the Connector section to whatever we decide, this needs
		// renaming too.
		this.intl.formatMsg('Error loading connectors', 'error.title.FETCH_COMPONENTS.500');
		this.intl.formatMsg('Unable to complete requested action', 'error.summary.FETCH_COMPONENTS');
		this.intl.formatMsg('Unknown error', 'error.detail.FETCH_COMPONENTS.generic');

		// FETCH_CREDENTIALS error strings
		this.intl.formatMsg('Error loading credentials', 'error.title.FETCH_CREDENTIALS.404');
		this.intl.formatMsg('Unable to complete requested action', 'error.summary.FETCH_CREDENTIALS');
		this.intl.formatMsg('The credentials failed to load', 'error.detail.FETCH_CREDENTIALS.404');
		this.intl.formatMsg('Unknown error', 'error.detail.FETCH_CREDENTIALS.generic');

		// FETCH_ENDPOINT error strings
		this.intl.formatMsg('Error loading specification', 'error.title.FETCH_ENDPOINT.404');
		this.intl.formatMsg('Unable to complete requested action', 'error.summary.FETCH_ENDPOINT');
		this.intl.formatMsg('The specification may no longer exist', 'error.detail.FETCH_ENDPOINT.404');
		this.intl.formatMsg('Unknown error', 'error.detail.FETCH_ENDPOINT.generic');

		// FETCH_MODELS error strings
		this.intl.formatMsg('Error loading models', 'error.title.FETCH_MODELS.404');
		this.intl.formatMsg('Unable to complete requested action', 'error.summary.FETCH_MODELS');
		this.intl.formatMsg('Error fetching the list of models', 'error.detail.FETCH_MODELS.404');
		this.intl.formatMsg('Unknown error', 'error.detail.FETCH_MODELS.generic');

		// FETCH_MODEL error strings
		this.intl.formatMsg('Error loading model', 'error.title.FETCH_MODEL.404');
		this.intl.formatMsg('Unable to complete requested action', 'error.summary.FETCH_MODEL');
		this.intl.formatMsg('The model may no longer exist', 'error.detail.FETCH_MODEL.404');
		this.intl.formatMsg('Unknown error', 'error.detail.FETCH_MODEL.generic');

		// EDIT_GENERATED_MODEL error strings
		this.intl.formatMsg('Not allowed', 'error.title.EDIT_GENERATED_MODEL.generic');
		this.intl.formatMsg('Unable to edit a generated model.', 'error.summary.EDIT_GENERATED_MODEL');
		this.intl.formatMsg('Models that have been automatically generated cannot be edited.', 'error.detail.EDIT_GENERATED_MODEL.generic');
		this.intl.formatMsg('Not allowed', 'error.title.EDIT_LEGACY_REDUCE_EXTEND_MODEL.generic');
		this.intl.formatMsg('Unable to edit a legacy reduce/extend model.', 'error.summary.EDIT_LEGACY_REDUCE_EXTEND_MODEL');
		this.intl.formatMsg('Models that use the legacy reduce/extend capabilities cannot be edited.', 'error.detail.EDIT_LEGACY_REDUCE_EXTEND_MODEL.generic');
		this.intl.formatMsg('Not allowed', 'error.title.EDIT_UNSUPPORTED_COMPOSITE_MODEL.generic');
		this.intl.formatMsg('Unable to edit this model.', 'error.summary.EDIT_UNSUPPORTED_COMPOSITE_MODEL');
		this.intl.formatMsg('This model is implemented in a way, or contains features which are unsupported.', 'error.detail.EDIT_UNSUPPORTED_COMPOSITE_MODEL.generic');

		// FETCH_PROJECT error strings
		this.intl.formatMsg('Error loading project', 'error.title.FETCH_PROJECT.404');
		this.intl.formatMsg('Unable to complete requested action', 'error.summary.FETCH_PROJECT');
		this.intl.formatMsg('The project file may no longer exist', 'error.detail.FETCH_PROJECT.404');
		this.intl.formatMsg('Unknown error', 'error.detail.FETCH_PROJECT.generic');

		// DELETE_ENDPOINT error strings
		this.intl.formatMsg('Not found', 'error.title.DELETE_ENDPOINT.generic');
		this.intl.formatMsg('Issue deleting the specification', 'error.summary.DELETE_ENDPOINT');

		// SAVE_ENDPOINT error strings
		this.intl.formatMsg('Error while importing the specification', 'error.title.SAVE_ENDPOINT.generic');
		this.intl.formatMsg('Error while importing the specification', 'error.detail.SAVE_ENDPOINT.generic');
		this.intl.formatMsg('Error while importing the specification', 'error.title.SAVE_ENDPOINT.500');
		this.intl.formatMsg('Check your permission to save files on disk', 'error.detail.SAVE_ENDPOINT.500');
		this.intl.formatMsg('Unable to save the file on disk', 'error.summary.SAVE_ENDPOINT');
	}

	componentDidMount() {
		this.props.fetchProject();
		this.props.fetchConfig();
		this.props.connectToNotifications();
	}

	render() {
		const { match: { path: basePath } } = this.props;
		const path = `${basePath}/project`;
		return (
			<Switch>
				<Route
					path={path}
					render={() => (
						<Switch>
							<Route path={`${path}/summary`} component={SummaryRoute} />
							<Route path={`${path}/flows`} component={FlowsRoute} />
							<Route path={`${path}/configuration`} component={ConfigurationRoute} />
							<Route path={`${path}/credentials`} component={CredentialsRoute} />
							<Route path={`${path}/plugins`} component={ComponentsRoute} />
							<Route path={`${path}/models`} component={ModelsRoute} />
							<Route path={`${path}/apidocs`} component={APIsRoute} />
							<Route path={`${path}/updates`} component={UpdatesRoute} />
							<Redirect to={`${path}/summary`} />
						</Switch>
					)}
				/>
				<Redirect to={`${path}/summary`} />
			</Switch>
		);
	}
}

ProjectRoute.propTypes = {
	location: PropTypes.shape({
		pathname: PropTypes.string.isRequired
	}).isRequired
};

function mapDispatchToProps(dispatch) {
	return {
		fetchConfig: bindActionCreators(system.actions, dispatch).fetchConfig,
		fetchProject: bindActionCreators(summary.actions, dispatch).fetchProject,
		connectToNotifications: bindActionCreators(navigation.actions, dispatch)
			.connectToNotifications,
		setLocation: bindActionCreators(location => ({
			type: 'SET_LOCATION',
			payload: location
		}), dispatch)
	};
}

export default compose(
	connect(
		null,
		mapDispatchToProps
	),
	injectIntl
)(ProjectRoute);
